import { useEffect, useState } from 'react';
import authService, { type CurrentUserProfile } from '../services/auth.service';
import { withRetry } from '../lib/retry';

/**
 * Subscribes to the cached `/auth/me` profile maintained by `authService`.
 *
 * The first caller after login pays the round-trip; every subsequent
 * caller reads the in-memory cache (deduped via the inflight promise),
 * so it's safe to use from many components simultaneously without
 * adding extra network load.
 *
 * Returns `null` while the profile is loading. Components that need a
 * derived flag (e.g. `isClientApproved`) should compose on top of this.
 */
export function useCurrentUserProfile(): CurrentUserProfile | null {
  const [profile, setProfile] = useState<CurrentUserProfile | null>(null);

  useEffect(() => {
    let cancelled = false;
    // Retried on transient failures (network, timeout, 5xx). A single blip used
    // to leave the profile null for the whole visit: the suspension banner
    // vanished for a suspended client, and approval-gated client pages stayed on
    // their loading state. A persistent failure still fails closed, and is listed
    // as known debt in docs/OPERATIONAL_READINESS.md.
    withRetry(() => authService.getCurrentUser(), { isCancelled: () => cancelled })
      .then((p) => {
        if (!cancelled) setProfile(p);
      })
      .catch((err: unknown) => {
        // Exhausted, or a non-transient error the axios interceptor already owns
        // (401). Gating components treat null as not-loaded / not-approved.
        console.warn('profile unavailable', err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return profile;
}

export type ClientApprovalGate = 'loading' | 'approved' | 'pending';

/** Three-way status for client post-KYC pages.
 *
 *  - `loading` — /auth/me hasn't returned yet; show a neutral loading
 *    indicator so the user never briefly sees the gate panel before it
 *    resolves to `approved`.
 *  - `approved` — caller is a client with `client_onboarding_state ===
 *    'approved'`, OR caller is staff (staff routes don't render these
 *    pages, but if they ever do they should never be gated).
 *  - `pending`  — caller is a client whose onboarding is not yet
 *    approved. Render the gate panel and skip the page's data fetch.
 */
export function useClientApprovalGate(): ClientApprovalGate {
  const profile = useCurrentUserProfile();
  if (profile === null) return 'loading';
  if (profile.role !== 'client') return 'approved';
  return profile.client_onboarding_state === 'approved' ? 'approved' : 'pending';
}
